<template>
    <span>
        <div class="card-body">

            <!-- View mode -->
            <template v-if="mode == 'view'">
                <div class="row mb-4">
                    <div class="col-sm-12">
                        <h5 class="mb-0">Inspection Details</h5>
                    </div>
                </div>

                <div class="row">
                    <div class="col-md-6 col-sm-12">
                        <div class="form-group">
                            <label class="text-primary mb-1">Inspection Area</label>
                            <p class="font-weight-medium mb-0">
                                <i v-if="assessment.area" :class="'las la-'+assessment.area.icon+' la-lg mr-1'"></i>
                                {{ areaTypeName }}
                            </p>
                        </div>
                    </div>
                    <div class="col-md-6 col-sm-12">
                        <div class="form-group">
                            <label class="text-primary mb-1">Area Label</label>
                            <p class="font-weight-medium mb-0">{{ assessment.label_house_area }}</p>
                        </div>
                    </div>
                </div>

                <div class="row">
                    <div class="col-md-6 col-sm-12">
                        <div class="form-group">
                            <label class="text-primary mb-1">Inspection Component</label>
                            <p class="font-weight-medium mb-0">{{ assessment.component ? assessment.component.name : '—' }}</p>
                        </div>
                    </div>
                    <div class="col-md-6 col-sm-12">
                        <div class="form-group">
                            <label class="text-primary mb-1">Component Label</label>
                            <p class="font-weight-medium mb-0">{{ assessment.label_house_component }}</p>
                        </div>
                    </div>
                </div>

                <div class="row mt-2">
                    <div class="col-sm-12">
                        <label class="text-primary mb-2">Defects Found</label>
                        <div v-if="assessment.selected_defects && assessment.selected_defects.length > 0">
                            <div
                                v-for="defect in assessment.selected_defects"
                                :key="defect.assessment_defect_id"
                                class="border rounded p-3 mb-2"
                            >
                                <p class="font-weight-medium mb-1">{{ defect.label }}</p>
                                <button
                                    v-if="defect.picture_url"
                                    type="button"
                                    class="defect-photo-btn"
                                    aria-label="View photo"
                                    @click="$refs.lightbox.open(defect.picture_url, { title: defect.label })"
                                >
                                    <img :src="defect.picture_url" class="img-fluid rounded defect-photo" alt="Defect photo">
                                    <span class="defect-photo-zoom"><i class="las la-search-plus"></i></span>
                                </button>
                                <span v-else class="text-muted small">No photo attached</span>
                            </div>
                        </div>
                        <p v-else class="text-muted">No defects recorded.</p>
                    </div>
                </div>
            </template>

            <!-- Edit mode -->
            <template v-if="mode == 'edit'">
                <div class="row mb-4">
                    <div class="col-sm-12">
                        <h5 class="mb-0">Edit Inspection</h5>
                    </div>
                </div>

                <!-- Area & Component are fixed for an existing assessment; shown read-only, matching view mode -->
                <div class="row">
                    <div class="col-md-6 col-sm-12">
                        <div class="form-group">
                            <label class="text-primary mb-1">Inspection Area</label>
                            <p class="font-weight-medium mb-0">
                                <i v-if="assessment.area" :class="'las la-'+assessment.area.icon+' la-lg mr-1'"></i>
                                {{ areaTypeName }}
                            </p>
                        </div>
                    </div>
                    <div class="col-md-6 col-sm-12">
                        <div class="form-group">
                            <label>Area Label</label>
                            <input
                                v-model="editForm.label_house_area"
                                type="text"
                                class="form-control"
                                list="edit-area-instance-suggestions"
                                autocomplete="off"
                                placeholder="i.e. Main Bedroom"
                                :class="{ 'is-invalid': $v.editForm.label_house_area.$error }"
                            >
                            <div class="invalid-feedback">Please give this room a name</div>
                            <datalist id="edit-area-instance-suggestions">
                                <option v-for="name in areaInstanceSuggestions" :key="name" :value="name"></option>
                            </datalist>
                        </div>
                    </div>
                </div>

                <div class="row">
                    <div class="col-md-6 col-sm-12">
                        <div class="form-group">
                            <label class="text-primary mb-1">Inspection Component</label>
                            <p class="font-weight-medium mb-0">{{ assessment.component ? assessment.component.name : '—' }}</p>
                        </div>
                    </div>
                    <div class="col-md-6 col-sm-12">
                        <div class="form-group">
                            <label>Component Label</label>
                            <input
                                v-model="editForm.label_house_component"
                                type="text"
                                class="form-control"
                                list="edit-component-instance-suggestions"
                                autocomplete="off"
                                placeholder="i.e. Main Bedroom Floor"
                                :class="{ 'is-invalid': $v.editForm.label_house_component.$error }"
                            >
                            <div class="invalid-feedback">Please give this part a name</div>
                            <datalist id="edit-component-instance-suggestions">
                                <option v-for="name in componentInstanceSuggestions" :key="name" :value="name"></option>
                            </datalist>
                        </div>
                    </div>
                </div>

                <!-- Defect selection -->
                <div class="row">
                    <div class="col-md-12 col-sm-12">
                        <div class="form-group mb-3">
                            <label>Choose defects</label>
                        </div>
                        <assessment-defect-group-selector
                            :groups="availableDefectGroups"
                            :selected-ids.sync="editForm.selected_defect_ids"
                            :existing-pictures="existingPictures"
                            @file-change="onFileChange"
                        />
                    </div>
                </div>
            </template>

        </div>

        <div class="card-footer">
            <div v-if="saving" class="progress mb-2" style="height: 6px;">
                <div class="progress-bar" role="progressbar" :style="{ width: uploadPercent + '%' }"></div>
            </div>
            <div class="d-flex justify-content-between">
                <button type="button" @click="$emit('go-back')" class="btn btn-outline-secondary btn-form" :disabled="saving">Back</button>
                <div>
                    <template v-if="mode == 'view'">
                        <button type="button" @click="enterEditMode" class="btn btn-primary btn-form">Edit</button>
                    </template>
                    <template v-if="mode == 'edit'">
                        <button type="button" @click="cancelEdit" class="btn btn-outline-secondary btn-form mr-2" :disabled="saving">Cancel</button>
                        <button type="button" @click="handleSubmit" class="btn btn-primary btn-form" :disabled="saving">
                            <template v-if="saving">
                                <span class="spinner-border spinner-border-sm mr-1" role="status"></span>
                                {{ uploadPercent < 100 ? 'Uploading ' + uploadPercent + '%' : 'Saving…' }}
                            </template>
                            <template v-else>Save</template>
                        </button>
                    </template>
                </div>
            </div>
        </div>

        <image-lightbox ref="lightbox" />
    </span>
</template>

<script>
import { required } from 'vuelidate/lib/validators';
import AssessmentDefectGroupSelector from './AssessmentDefectGroupSelector.vue';
import focusFirstError from '../../utils/focusFirstError';

export default {
    props: ['assessmentId'],

    components: { AssessmentDefectGroupSelector },

    data() {
        return {
            mode: 'view',
            loading: false,
            assessment: {},
            editForm: {
                house_area: null,
                house_area_other: null,
                house_component: null,
                label_house_area: '',
                label_house_component: '',
                selected_defect_ids: [],
            },
            // maps component_defect_id → assessment_defect_id for already-saved defects
            existingDefectMap: {},
            availableDefectGroups: [],
            // files[component_defect_id] = File object
            files: {},
            areaInstanceSuggestions: [],
            componentInstanceSuggestions: [],
            saving: false,
            uploadPercent: 0,
        };
    },

    validations: {
        editForm: {
            label_house_area: { required },
            label_house_component: { required },
        },
    },

    computed: {
        areaTypeName() {
            if (this.assessment.area) {
                return this.assessment.area.name;
            }
            return this.assessment.house_area_other || '—';
        },
        existingPictures() {
            const map = {};
            (this.assessment.selected_defects || []).forEach(d => {
                if (d.picture_url) {
                    map[d.component_defect_id] = d.picture_url;
                }
            });
            return map;
        },
    },

    mounted() {
        this.loadAssessment();
    },

    methods: {
        async loadAssessment() {
            try {
                const { data } = await axios.get(route('owner.assessments.show', this.assessmentId));
                this.assessment = data;
            } catch (e) {
                console.error(e);
                this.$swal('Error!', 'Could not load inspection.', 'error');
            }
        },

        async loadDefectsForComponent(componentId) {
            try {
                const { data } = await axios.get(route('owner.assessments.components.defects.list', componentId));
                this.availableDefectGroups = data;
            } catch (e) {
                console.error(e);
            }
        },

        enterEditMode() {
            // Area & Component are fixed once an assessment exists — populate directly from it.
            // Rooms the owner typed in as "Other" have no catalogue area.
            this.editForm.house_area       = this.assessment.area || { id: 'other' };
            this.editForm.house_area_other = this.assessment.house_area_other;
            this.editForm.house_component  = this.assessment.component;
            this.editForm.label_house_area      = this.assessment.label_house_area;
            this.editForm.label_house_component = this.assessment.label_house_component;

            this.editForm.selected_defect_ids = (this.assessment.selected_defects || []).map(d => d.component_defect_id);

            // Map kept defects so the backend can diff new vs. existing
            this.existingDefectMap = {};
            (this.assessment.selected_defects || []).forEach(d => {
                this.existingDefectMap[d.component_defect_id] = d.assessment_defect_id;
            });

            this.files = {};

            if (this.assessment.component) {
                this.loadDefectsForComponent(this.assessment.component.id);
            }

            this.loadAreaInstanceSuggestions();
            this.loadComponentInstanceSuggestions();

            this.$v.$reset();
            this.mode = 'edit';
        },

        async loadAreaInstanceSuggestions() {
            if (!this.assessment.house || !this.assessment.area) return;
            try {
                const { data } = await axios.get(route('owner.assessments.areaInstances.list', [this.assessment.house, this.assessment.area.id]));
                this.areaInstanceSuggestions = data;
            } catch (e) {
                console.error(e);
            }
        },

        async loadComponentInstanceSuggestions() {
            if (!this.assessment.house_area_instance_id || !this.assessment.component) return;
            try {
                const { data } = await axios.get(route('owner.assessments.componentInstances.list', [this.assessment.house_area_instance_id, this.assessment.component.id]));
                this.componentInstanceSuggestions = data;
            } catch (e) {
                console.error(e);
            }
        },

        cancelEdit() {
            this.mode = 'view';
        },

        onFileChange({ componentDefectId, file }) {
            if (file) {
                this.$set(this.files, componentDefectId, file);
            } else {
                this.$delete(this.files, componentDefectId);
            }
        },

        messageFromError(e) {
            const response = e && e.response;
            if (!response) {
                return 'No internet connection. Your changes are still here, please try again.';
            }
            if (response.status === 413) {
                return 'The photos are too large to upload. Try fewer or smaller photos.';
            }
            if (response.status === 422 && response.data && response.data.errors) {
                return Object.values(response.data.errors)[0][0];
            }
            return (response.data && response.data.message) || 'Could not save inspection.';
        },

        async handleSubmit() {
            this.$v.editForm.$touch();
            if (this.$v.editForm.$anyError) {
                this.$nextTick(() => focusFirstError(this.$el, { swal: this.$swal }));
                return;
            }

            const formData = new FormData();
            formData.append('_method', 'PUT');

            // Build house_defects for the backend, reattaching existing_defect_id
            const house_defects = this.editForm.selected_defect_ids.map(cdId => ({
                component_defect_id: cdId,
                existing_defect_id:  this.existingDefectMap[cdId] || null,
            }));
            formData.append('form', JSON.stringify(Object.assign({}, this.editForm, { house_defects })));

            this.editForm.selected_defect_ids.forEach(cdId => {
                const file = this.files[cdId];
                if (file) {
                    formData.append('file' + cdId, file);
                }
            });

            this.saving = true;
            this.uploadPercent = 0;
            try {
                await axios.post(
                    route('owner.assessments.update', this.assessmentId),
                    formData,
                    {
                        onUploadProgress: (event) => {
                            if (event.total) {
                                this.uploadPercent = Math.round((event.loaded / event.total) * 100);
                            }
                        },
                    }
                );
            } catch (e) {
                this.saving = false;
                this.$swal('Not saved', this.messageFromError(e), 'error');
                return;
            }

            this.saving = false;
            await this.loadAssessment();
            this.mode = 'view';
            this.$swal({
                toast: true,
                position: 'top',
                icon: 'success',
                title: 'Inspection updated',
                showConfirmButton: false,
                timer: 2500,
            });
        },
    },
};
</script>

<style scoped>
.defect-photo-btn {
    position: relative;
    display: inline-block;
    padding: 0;
    border: 0;
    background: none;
    line-height: 0;
}
.defect-photo {
    max-height: 150px;
}
.defect-photo-zoom {
    position: absolute;
    right: 6px;
    bottom: 6px;
    width: 28px;
    height: 28px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background: rgba(0, 0, 0, 0.55);
    color: #fff;
    font-size: 16px;
    line-height: 1;
}
</style>
